import { useCallback } from "react";
import { has, includes, isEmpty, isObject, map, reduce, some } from "lodash";
import { graphql, useStaticQuery } from "gatsby";

import useAllFormData from "~/hooks/data/useAllFormData";
import { useLocationContext } from "~/context/LocationContext";
import { useMarketingContext } from "~/context/MarketingContext";

import {
    removeLocalStorageItem,
    getLocalStorageSerialize,
    addLocalStorageListItem,
    getLocalStorageItem,
    setLocalStorageSerialize,
} from "~/utilities/localStorageHelpers";

import {
    getPresetFormValues,
    submitFormData,
    formatDataForSubmission,
    isInvalidISPEmail,
    saveInvalidEmailDomain,
    FormSubmissionException,
} from "~/utilities/formHelpers";

import { getUTCFormat } from "~/utilities/formatDates";
import { getFileUrl } from "~/utilities/helpers";

const addProspectId = (hiddenValues, userData, pardotFieldNames) => {
    return reduce(
        hiddenValues,
        (acc, item, key) => {
            const predicate = (pardotKey) => includes(key, pardotKey);

            if (some(pardotFieldNames, predicate)) {
                try {
                    const jsonData = JSON.parse(item);

                    const hiddenData = map(jsonData, (data) => {
                        const prospectId = userData.prospect_id;
                        return {
                            ...data,
                            id: prospectId,
                        };
                    });

                    const newJsonData = JSON.stringify(hiddenData);

                    return {
                        ...acc,
                        [key]: newJsonData,
                    };
                } catch (e) {
                    console.error(e.message);
                    return {
                        ...acc,
                        [key]: item,
                    };
                }
            }

            return {
                ...acc,
                [key]: item,
            };
        },
        {},
    );
};

const shouldSubmit = (storageKey, formId, userData) => {
    const pardotHistory = getLocalStorageSerialize(storageKey, []);
    const isInValidEmail = isInvalidISPEmail(formId, userData.email);
    const cookiesAccepted = getLocalStorageItem("cookiesAccepted");

    return (
        cookiesAccepted === "true" &&
        formId &&
        has(userData, "email") &&
        has(userData, "prospect_id") &&
        !isEmpty(pardotHistory) &&
        !isInValidEmail
    );
};

const storeDocument = (doc, storageKey) => {
    if (isObject(doc)) {
        const newDoc = {
            id: doc.id,
            title: doc.title,
            url: getFileUrl(doc),
            timestamp: getUTCFormat(new Date()),
        };

        addLocalStorageListItem(storageKey, newDoc);
    }
};

const storeProductData = (data, storageKey) => {
    if (isObject(data)) {
        const newData = {
            ...data,
            timestamp: getUTCFormat(new Date()),
        };

        addLocalStorageListItem(storageKey, newData);
    }
};

const storePageViewData = (title, url, storageKey) => {
    if (title && url) {
        const newPageView = {
            articleTitle: title,
            pageUrl: url,
            timestamp: getUTCFormat(new Date()),
        };

        const currentItems = getLocalStorageSerialize(storageKey) ?? [];

        const newItems = currentItems.concat(newPageView);

        setLocalStorageSerialize(storageKey, newItems);
    }
};

const usePardotTracking = () => {
    const { getFormById } = useAllFormData();

    const { location } = useLocationContext();

    const { hiddenFieldValues, userData } = useMarketingContext();

    const {
        wp: { optionsPage, formsOptionsPage },
    } = useStaticQuery(graphql`
        query usePardotTrackingQuery {
            wp {
                optionsPage {
                    globalOptions {
                        gravityForms
                    }
                }
                formsOptionsPage {
                    pardotHistoryFormId {
                        downloadHistoryForm {
                            formId
                            pardotFieldName
                        }
                        buyOnlineHistoryForm {
                            formId
                            pardotFieldName
                        }
                        pageViewForm {
                            formId
                            pardotFieldName
                        }
                    }
                }
            }
        }
    `);

    const sendPardotData = useCallback(
        (formId, storageKey) => {
            const getFormData = () => {
                const formInformation = getFormById(formId);

                const hiddenValues = userData
                    ? getPresetFormValues(
                          formInformation,
                          userData,
                          hiddenFieldValues,
                          formId,
                      )
                    : null;

                const dataWithProspectId = addProspectId(
                    hiddenValues,
                    userData,
                );

                return formatDataForSubmission(
                    dataWithProspectId,
                    formInformation,
                );
            };

            const formattedData = getFormData();

            const endpoint = optionsPage?.globalOptions?.gravityForms || "";

            submitFormData(formId, formattedData, true, endpoint)
                .then((response) => {
                    if (response?.code === "success") {
                        removeLocalStorageItem(storageKey);
                    } else {
                        const formattedData = getFormData();

                        const formData = {
                            ...formattedData,
                            formId: formId,
                        };

                        const error = new FormSubmissionException(
                            response,
                            formData,
                        );

                        saveInvalidEmailDomain(
                            error.errorMessage,
                            error.formData,
                        );

                        console.error(error.errorMessage);
                    }
                })
                .catch((error) => {
                    console.error(`Issues postHistoryData - ${error.message}`);
                });
        },
        [
            userData,
            getFormById,
            hiddenFieldValues,
            optionsPage?.globalOptions?.gravityForms,
        ],
    );

    const onBuyOnlineSubmission = useCallback(
        (data = null) => {
            const pardotForms = formsOptionsPage?.pardotHistoryFormId ?? {};
            const form = pardotForms?.buyOnlineHistoryForm ?? {};
            const formId = form.formId;
            const storageKey = form.pardotFieldName;

            if (data) {
                storeProductData(data, storageKey);
            }

            if (shouldSubmit(storageKey, formId, userData)) {
                sendPardotData(formId, storageKey);
            }
        },
        [sendPardotData, formsOptionsPage?.pardotHistoryFormId, userData],
    );

    const onDownloadSubmission = useCallback(
        (doc = null) => {
            const pardotForms = formsOptionsPage?.pardotHistoryFormId ?? {};
            const form = pardotForms?.downloadHistoryForm ?? {};
            const formId = form.formId;
            const storageKey = form.pardotFieldName;

            if (doc) {
                storeDocument(doc, storageKey);
            }

            if (shouldSubmit(storageKey, formId, userData)) {
                sendPardotData(formId, storageKey);
            }
        },
        [sendPardotData, formsOptionsPage?.pardotHistoryFormId, userData],
    );

    const onPageViewSubmission = useCallback(() => {
        const pageUrl = location.href;
        const currentUrl = hiddenFieldValues.pageUrl;
        const pageTitle = hiddenFieldValues.pageTitle;
        const articleTitle = hiddenFieldValues.articleTitle;
        const title = pageTitle ?? articleTitle;
        const pardotForms = formsOptionsPage?.pardotHistoryFormId ?? {};
        const form = pardotForms?.pageViewForm ?? {};
        const formId = form.formId;
        const storageKey = form.pardotFieldName;
        const isCurrentUrl = title && pageUrl === currentUrl;

        if (isCurrentUrl) {
            storePageViewData(title, pageUrl, storageKey);

            if (shouldSubmit(storageKey, formId, userData)) {
                sendPardotData(formId, storageKey);
            }
        }
    }, [
        sendPardotData,
        formsOptionsPage?.pardotHistoryFormId,
        userData,
        hiddenFieldValues.pageTitle,
        hiddenFieldValues.articleTitle,
        hiddenFieldValues.pageUrl,
        location.href,
    ]);

    const onPageLoad = useCallback(() => {
        onBuyOnlineSubmission();
        onDownloadSubmission();
        onPageViewSubmission();
    }, [onBuyOnlineSubmission, onDownloadSubmission, onPageViewSubmission]);

    return {
        onPageLoad,
        onDownloadSubmission,
        onBuyOnlineSubmission,
    };
};

export default usePardotTracking;
